import { useState, useEffect, useRef, useCallback } from 'react';
import { calendarService } from '../services/api';

const POLL_INTERVAL_MS = 60000; // 60 seconds

/**
 * Hook that polls Google Calendar for new/updated events.
 * - Calls POST /calendar/sync/poll on mount
 * - If user has no Google Calendar connected, stops (no interval)
 * - Otherwise, polls every 60 seconds
 * - Accepts onSyncComplete(importedCount) callback, called only when events were imported
 * - Returns { lastSynced, isSyncing }
 */
const useGoogleCalendarSync = ({ onSyncComplete } = {}) => {
  const [lastSynced, setLastSynced] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const intervalRef = useRef(null);
  const connectedRef = useRef(false);
  const onSyncCompleteRef = useRef(onSyncComplete);

  // Keep the callback ref up to date without causing re-renders
  useEffect(() => {
    onSyncCompleteRef.current = onSyncComplete;
  }, [onSyncComplete]);

  const poll = useCallback(async () => {
    setIsSyncing(true);
    try {
      const response = await calendarService.googleCalendar.poll();
      const data = response.data;

      if (!data.synced && data.reason === 'not_connected') {
        // User has no Google Calendar connected — stop polling
        connectedRef.current = false;
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        return;
      }

      connectedRef.current = true;

      if (data.synced) {
        setLastSynced(new Date());
        if ((data.imported > 0 || data.updated > 0) && onSyncCompleteRef.current) {
          onSyncCompleteRef.current(data.imported + data.updated);
        }
      }
    } catch (error) {
      console.error('Google Calendar poll error:', error);
    } finally {
      setIsSyncing(false);
    }
  }, []);

  useEffect(() => {
    // Initial poll on mount
    poll();

    // Set up interval — will be cleared if not_connected response is received
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [poll]);

  return { lastSynced, isSyncing };
};

export default useGoogleCalendarSync;
